import React, { useEffect, useState } from "react";
import { Button, Box, useColorModeValue } from "@chakra-ui/react";
import { db } from "../firebase.config";
import { collection, getDocs } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import FadeSpinner from "../components/feedback/FadeSpinner";
import { ImLocation } from "react-icons/im";
import { FaMotorcycle } from "react-icons/fa";
import moment from "moment";
import { Helmet } from "react-helmet";

const SeeAllFeaturedBikes = () => {
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const cardBg = useColorModeValue("white", "gray.800");
  const shadowColor = useColorModeValue(
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
  );

  useEffect(() => {
    const fetchBikes = async () => {
      try {
        setLoading(true);
        const carsRef = collection(db, "featured-bikes");
        const qSnap = await getDocs(carsRef);
        const bikesArray = [];
        qSnap.forEach((doc) => {
          bikesArray.push({ id: doc.id, ...doc.data() });
        });
        setBikes(bikesArray);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError("Failed to load featured bikes.");
      }
    };

    fetchBikes();
  }, []);

  const checkDetails = (id) => {
    navigate(`/category/sale/${id}?type=featured-bike`);
  };

  if (loading) return <FadeSpinner />;
  if (error) return <Box className="text-center text-red-500 p-8">{error}</Box>;
  if (bikes.length === 0) return (
    <Box className="text-center p-8">
      <h1 className="text-2xl text-gray-600">No featured bikes available</h1>
      <Link to="/" className="text-blue-500 hover:underline">Browse other bikes</Link>
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>Featured Motorcycles | NCP Wheels</title>
        <meta
          name="keywords"
          content="ncp cars, non custom cars, ncp car, non custom paid cars, ncp cars for sale in swat, ncp cars for sale in swat pakistan, ncp car for sale in swat, ncp cars for sale in pakistan, ncp cars in pakistan for sale, ncp car for sale in pakistan, pakistan non custom cars for sale, non custom car for sale in pakistan, non custom cars in pakistan for sale, non custom cars for sale in pakistan, ncp car for sale, ncp cars for sale in swat olx, ncp cars for sale in chaman, ncp cars in swat, non custom paid cars in chaman, chaman border cars, ncp cars swat, swat ncp cars, non custom paid cars chaman, non custom cars in chaman, chaman non custom cars, chaman border non custom cars price, ncp cars in pakistan, cars for sale in swat, ncp cars swat chakdara, ncp swat chakdara motors, car for sale in swat, chaman border cars for sale in pakistan, non custom car price in chaman"
        />
      </Helmet>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              Featured Motorcycles
            </h1>
            <div className="w-20 h-1 bg-blue-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {bikes.map((bike) => (
              <div 
                key={bike.id}
                className={`bg-${cardBg} rounded-xl overflow-hidden border border-opacity-20 border-gray-300 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl`}
                style={{ boxShadow: shadowColor }}
              >
                {/* Image with hover effect */}
                <div className="relative group overflow-hidden h-48">
                  <img
                    src={bike.carImages?.[0] || "/placeholder-bike.jpg"}
                    alt={bike.modelName}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Badges */}
                  <div className="absolute top-3 right-3 flex space-x-2">
                    {bike.offer && (
                      <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full shadow-md transform hover:scale-110 transition-transform">
                        Offer
                      </span>
                    )}
                    <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full shadow-md">
                      Featured
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {bike.modelName}
                      </h2>
                      <p className="text-blue-600 text-sm">{bike.manufacturer}</p>
                    </div>
                    {bike?.timestamp && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                        {moment(bike?.timestamp?.toDate()).fromNow()}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center text-gray-600 mb-3">
                    <ImLocation className="inline mr-1 text-blue-500" size={14} />
                    <span className="text-sm">{bike.location || bike.city}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="bg-blue-50 p-2 rounded-lg">
                      <p className="text-xs text-gray-700">
                        <span className="font-semibold">Mileage:</span> {bike.mileage || 'N/A'} Km
                      </p>
                    </div>
                    <div className="bg-blue-50 p-2 rounded-lg">
                      <p className="text-xs text-gray-700">
                        <span className="font-semibold">Year:</span> {bike.modelYear || 'N/A'}
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mb-4">
                    <p className="text-lg font-bold text-blue-700">
                      PKR {bike.offer ? bike.discountedPrice : bike.price}
                    </p>
                    {bike.offer && (
                      <p className="text-sm line-through text-gray-500">
                        PKR {bike.price}
                      </p>
                    )}
                  </div>

                  <Button
                    onClick={() => checkDetails(bike.id)}
                    width="full"
                    className="transition-all duration-300 transform hover:scale-[1.02] hover:shadow-md"
                    bgGradient="linear(to-r, blue.500, blue.600)"
                    color="white"
                    leftIcon={<FaMotorcycle />}
                    _hover={{
                      bgGradient: "linear(to-r, blue.600, blue.700)",
                      transform: "translateY(-1px)",
                      boxShadow: "md",
                    }}
                    _active={{
                      bgGradient: "linear(to-r, blue.700, blue.800)",
                    }}
                    size="md"
                    borderRadius="lg"
                    boxShadow="sm"
                  >
                    View Details
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default SeeAllFeaturedBikes;